import React, { useState } from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { MailIcon, LockIcon, UserIcon, BookIcon, UsersIcon } from '../components/Icons';
import { API_ENDPOINTS } from '../config/api';

interface FormErrors {
  accountType?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
  displayName?: string;
  terms?: string;
  general?: string;
}

export const RegisterPage = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    accountType: '',
    email: '',
    password: '',
    confirmPassword: '',
    displayName: '',
    termsAccepted: false,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);

  const validateStep = () => {
    const newErrors: FormErrors = {};

    if (step === 1) {
      if (!formData.accountType) {
        newErrors.accountType = 'Please select an account type';
      }
    }

    if (step === 2) {
      if (!formData.email) {
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email';
      }

      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      } else if (!/[A-Z]/.test(formData.password)) {
        newErrors.password = 'Password must contain at least one uppercase letter';
      } else if (!/[a-z]/.test(formData.password)) {
        newErrors.password = 'Password must contain at least one lowercase letter';
      } else if (!/[0-9]/.test(formData.password)) {
        newErrors.password = 'Password must contain at least one number';
      }

      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }

      if (!formData.displayName) {
        newErrors.displayName = 'Display name is required';
      }
    }

    if (step === 3) {
      if (!formData.termsAccepted) {
        newErrors.terms = 'You must accept the terms to continue';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep()) {
      if (step < 3) {
        setStep(step + 1);
      } else {
        handleSubmit();
      }
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setErrors({});

    try {
      // Map frontend account type to backend role format
      const roleMap: Record<string, string> = {
        writer: 'WRITER',
        reader: 'READER',
        agent: 'AGENT',
      };

      const response = await fetch(API_ENDPOINTS.auth.register, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          displayName: formData.displayName,
          role: roleMap[formData.accountType],
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle validation errors
        if (data.details) {
          const newErrors: FormErrors = {};
          data.details.forEach((detail: { field: string; message: string }) => {
            newErrors[detail.field as keyof FormErrors] = detail.message;
          });
          setErrors(newErrors);
        } else {
          setErrors({ general: data.error || 'Registration failed' });
        }
        setLoading(false);
        return;
      }

      // Store token in localStorage
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));

      console.log('Registration successful:', data);

      // Redirect to email verification page
      window.location.href = '/verify-email';
    } catch (error) {
      console.error('Registration error:', error);
      setErrors({ general: 'Network error. Please check your connection and try again.' });
      setLoading(false);
    }
  };

  const accountTypes = [
    {
      type: 'writer',
      title: 'Writer',
      icon: <BookIcon width={32} height={32} />,
      description: 'Submit your manuscript and find beta readers',
      features: ['Post manuscripts', 'Find beta readers', 'Query agents', 'Track submissions'],
    },
    {
      type: 'reader',
      title: 'Beta Reader',
      icon: <UsersIcon width={32} height={32} />,
      description: 'Read and provide feedback on manuscripts',
      features: ['Browse manuscripts', 'Apply to read', 'Provide feedback', 'Build portfolio'],
    },
    {
      type: 'agent',
      title: 'Agent',
      icon: <UserIcon width={32} height={32} />,
      description: 'Manage queries and discover new talent',
      features: ['Create MSWL', 'Receive queries', 'Request materials', 'Manage submissions'],
    },
  ];

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.fonts.primary,
    }}>
      <Header showAuth={false} />

      <main style={{
        maxWidth: '900px',
        margin: '0 auto',
        padding: '60px 40px',
      }}>
        {/* Progress Indicator */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '40px',
        }}>
          {[1, 2, 3].map((num) => (
            <React.Fragment key={num}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: step >= num ? theme.colors.primary : theme.colors.border,
                color: step >= num ? theme.colors.white : theme.colors.textSecondary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '500',
                fontSize: '14px',
                fontFamily: theme.fonts.monospace,
              }}>
                {num}
              </div>
              {num < 3 && (
                <div style={{
                  width: '60px',
                  height: '2px',
                  backgroundColor: step > num ? theme.colors.primary : theme.colors.border,
                }}></div>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step Titles */}
        <div style={{
          textAlign: 'center',
          marginBottom: '40px',
        }}>
          <h1 style={{
            fontSize: '40px',
            fontWeight: '400',
            marginBottom: '12px',
            color: theme.colors.textPrimary,
          }}>
            {step === 1 && 'Choose Your Account Type'}
            {step === 2 && 'Create Your Account'}
            {step === 3 && 'Terms & Conditions'}
          </h1>
          <p style={{
            fontSize: '18px',
            color: theme.colors.textSecondary,
          }}>
            {step === 1 && 'Select the type that best describes you'}
            {step === 2 && 'Enter your details to get started'}
            {step === 3 && 'Review and accept our terms'}
          </p>
        </div>

        {/* Step 1: Account Type Selection */}
        {step === 1 && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '24px',
            marginBottom: '32px',
          }}>
            {accountTypes.map((account) => (
              <div
                key={account.type}
                onClick={() => setFormData({ ...formData, accountType: account.type })}
                style={{
                  backgroundColor: theme.colors.white,
                  border: `2px solid ${formData.accountType === account.type ? theme.colors.primary : theme.colors.border}`,
                  borderRadius: theme.borderRadius.xlarge,
                  padding: '32px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  textAlign: 'center',
                }}
              >
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '64px',
                  height: '64px',
                  backgroundColor: formData.accountType === account.type ? theme.colors.primaryLight : theme.colors.backgroundAlt,
                  borderRadius: theme.borderRadius.large,
                  color: theme.colors.primary,
                  marginBottom: '20px',
                }}>
                  {account.icon}
                </div>
                <h3 style={{
                  fontSize: '24px',
                  fontWeight: '500',
                  marginBottom: '12px',
                  color: theme.colors.textPrimary,
                }}>
                  {account.title}
                </h3>
                <p style={{
                  fontSize: '16px',
                  color: theme.colors.textSecondary,
                  marginBottom: '20px',
                }}>
                  {account.description}
                </p>
                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  textAlign: 'left',
                }}>
                  {account.features.map((feature, idx) => (
                    <li key={idx} style={{
                      fontSize: '14px',
                      color: theme.colors.textSecondary,
                      marginBottom: '8px',
                      paddingLeft: '20px',
                      position: 'relative',
                    }}>
                      <span style={{
                        position: 'absolute',
                        left: 0,
                        color: theme.colors.primary,
                      }}>•</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        {/* Step 2: Account Details */}
        {step === 2 && (
          <div style={{
            maxWidth: '500px',
            margin: '0 auto',
            backgroundColor: theme.colors.white,
            borderRadius: theme.borderRadius.xlarge,
            padding: '40px',
            border: `1px solid ${theme.colors.border}`,
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
            }}>
              <Input
                label="Display Name"
                type="text"
                placeholder="Enter your name"
                value={formData.displayName}
                onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                error={errors.displayName}
                icon={<UserIcon />}
                required
              />

              <Input
                label="Email Address"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                error={errors.email}
                icon={<MailIcon />}
                required
              />

              <Input
                label="Password"
                type="password"
                placeholder="At least 8 characters"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                error={errors.password}
                icon={<LockIcon />}
                required
              />

              <Input
                label="Confirm Password"
                type="password"
                placeholder="Re-enter your password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                error={errors.confirmPassword}
                icon={<LockIcon />}
                required
              />
            </div>
          </div>
        )}

        {/* Step 3: Terms & Conditions */}
        {step === 3 && (
          <div style={{
            maxWidth: '700px',
            margin: '0 auto',
            backgroundColor: theme.colors.white,
            borderRadius: theme.borderRadius.xlarge,
            padding: '40px',
            border: `1px solid ${theme.colors.border}`,
          }}>
            <div style={{
              backgroundColor: theme.colors.backgroundAlt,
              borderRadius: theme.borderRadius.large,
              padding: '24px',
              marginBottom: '24px',
              maxHeight: '300px',
              overflowY: 'auto',
              fontSize: '15px',
              lineHeight: '1.6',
              color: theme.colors.textSecondary,
            }}>
              <h4 style={{ color: theme.colors.textPrimary, marginBottom: '16px' }}>Terms of Service</h4>
              <p style={{ marginBottom: '12px' }}>
                By using Manuscript Match, you agree to the following terms:
              </p>
              <ul style={{ paddingLeft: '20px', marginBottom: '12px' }}>
                <li>You are at least 18 years old</li>
                <li>You will respect intellectual property rights</li>
                <li>You will provide honest and constructive feedback</li>
                <li>You will not share manuscripts without permission</li>
                <li>You will maintain professional conduct at all times</li>
              </ul>
              <h4 style={{ color: theme.colors.textPrimary, marginBottom: '16px', marginTop: '20px' }}>Privacy Policy</h4>
              <p>
                Your data is secure with us. We will never share your personal information
                or manuscripts with third parties without your explicit consent. Read our
                full privacy policy for more details.
              </p>
            </div>

            <label style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              cursor: 'pointer',
              fontSize: '15px',
              color: theme.colors.textPrimary,
            }}>
              <input
                type="checkbox"
                checked={formData.termsAccepted}
                onChange={(e) => setFormData({ ...formData, termsAccepted: e.target.checked })}
                style={{
                  width: '20px',
                  height: '20px',
                  marginTop: '2px',
                  cursor: 'pointer',
                }}
              />
              <span>
                I have read and agree to the Terms of Service and Privacy Policy
              </span>
            </label>
            {errors.terms && (
              <p style={{ color: theme.colors.error, fontSize: '14px', marginTop: '8px' }}>
                {errors.terms}
              </p>
            )}
          </div>
        )}

        {/* General Error Message */}
        {errors.general && (
          <div style={{
            maxWidth: '500px',
            margin: '0 auto 20px',
            padding: '16px',
            backgroundColor: theme.colors.errorLight,
            border: `1px solid ${theme.colors.error}`,
            borderRadius: theme.borderRadius.medium,
            color: theme.colors.error,
            fontSize: '15px',
            textAlign: 'center',
          }}>
            {errors.general}
          </div>
        )}

        {/* Navigation Buttons */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '16px',
          marginTop: '32px',
        }}>
          {step > 1 && (
            <Button variant="secondary" onClick={() => setStep(step - 1)} disabled={loading}>
              Back
            </Button>
          )}
          <Button onClick={handleNext} disabled={loading}>
            {loading ? 'Creating Account...' : (step === 3 ? 'Create Account' : 'Continue')}
          </Button>
        </div>

        {/* Login Link */}
        <div style={{
          textAlign: 'center',
          marginTop: '32px',
          fontSize: '15px',
          color: theme.colors.textSecondary,
        }}>
          Already have an account?{' '}
          <a href="/login" style={{
            color: theme.colors.primary,
            textDecoration: 'none',
            fontWeight: '500',
          }}>
            Sign in
          </a>
        </div>
      </main>

      <Footer />
    </div>
  );
};
